/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import './index.css';
import 'react-toastify/dist/ReactToastify.css';

import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { Fragment } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';

import store from 'store';

import RootContainer from './root';
import {
	getPropagationTargets,
	REPLAYS_ON_ERROR_SAMPLE_RATE,
	REPLAYS_SESSION_SAMPLE_RATE,
	SENTRY_INTEGRATIONS,
	TRACES_SAMPLE_RATE
} from './sentry/constants';
import * as serviceWorker from './serviceWorker';
import theme from './theme';

posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
	api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST
});

Sentry.init({
	dsn: process.env.REACT_APP_SENTRY_DSN,
	environment: process.env.REACT_APP_SENTRY_ENV,
	integrations: SENTRY_INTEGRATIONS,
	// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
	tracePropagationTargets: getPropagationTargets(),
	// Performance Monitoring
	tracesSampleRate: TRACES_SAMPLE_RATE,
	// Session Replay
	replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
	replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE
});

// * Backend must allow sentry headers on their side to avoid CORS issues
// * "Access-Control-Allow-Headers: sentry-trace"
// * "Access-Control-Allow-Headers: baggage"

const rootElement = document.getElementById('root');

const root = createRoot(rootElement);

root.render(
	<Fragment>
		<ThemeProvider theme={theme}>
			<PostHogProvider client={posthog}>
				<Provider store={store}>
					<RootContainer />
					<ToastContainer />
				</Provider>
			</PostHogProvider>
		</ThemeProvider>
	</Fragment>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
